import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Link, Typography } from "@mui/material";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import { useShipments } from "../../../api/shipping";
import { useBreadcrumbs } from "../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../components/ui/detailView";
import EntityReferenceLink from "../../../components/ui/entityReference/entityReferenceLink.component";
import { StatusChip } from "../../../components/ui/statusChip/statusChip.component";
import { SHIPMENT_IN_FLIGHT_STATUSES, SHIPMENT_STATUS_COLOR_MAP } from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { Shipment } from "../../../types/shipping";
import { formatDateTimeInline, formatMoney, formatQuantity } from "../../../utils";
import { countryName } from "../../../utils/countries";
import { buildOrderedPartReferenceId } from "../../../utils/entityRoutes";

// While a worker orders or cancels the shipment, the page checks back this often.
const IN_FLIGHT_POLL_MS = 5000;

const TRACKING_STATE_LABEL_KEYS: Record<string, string> = {
  waiting: "Waiting for pickup",
  ordered: "Waiting for pickup",
  "collect-problem": "Pickup problem",
  collected: "Collected by the courier",
  transit: "In transit",
  delivery: "Out for delivery",
  "ready-for-transport": "In transit",
  "waiting-for-receiver": "Waiting for the receiver",
  "delivery-problem": "Delivery problem",
  delivered: "Shipment delivered",
  "unexpected-situation": "Unexpected situation",
  canceled: "Cancelled",
  returned: "Returned",
};

interface ShipmentBasicInfoProps {
  /** Shown inside a preview modal rather than on its own page. */
  previewId?: string | number;
}

export const ShipmentBasicInfo = ({ previewId }: ShipmentBasicInfoProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const id = previewId ?? params.id;
  const { setOverride } = useBreadcrumbs();
  const { getShipment } = useShipments();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const canViewShipments = useHasPermission("shipping.view_shipment");
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  const lastStatus = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const { data, success } = await getShipment(id);
    if (success) {
      setShipment(data);
      // The toolbar fetches the shipment on its own; a change seen by the
      // polling here (ordered, cancelled) is passed on so its actions follow.
      if (lastStatus.current && lastStatus.current !== data.status) {
        window.dispatchEvent(new Event("shipmentStatusChanged"));
      }
      lastStatus.current = data.status;
      if (!previewId) {
        setOverride(`/service/shipments/${id}`, data.waybill || `${t("Shipment")} #${data.id}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, previewId, getShipment]);

  useEffect(() => {
    load();
    window.addEventListener("shipmentUpdated", load);
    return () => window.removeEventListener("shipmentUpdated", load);
  }, [load]);

  const inFlight = shipment ? SHIPMENT_IN_FLIGHT_STATUSES.includes(shipment.status) : false;
  useEffect(() => {
    if (!inFlight) return;
    const timer = window.setInterval(load, IN_FLIGHT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [inFlight, load]);

  if (!canViewShipments) return null;
  if (!shipment) return <DetailPage loading />;

  const receiverLabel = shipment.receiver_company || shipment.receiver_name;

  return (
    <DetailPage>
      <DetailHeader
        icon={<LocalShippingOutlined />}
        title={shipment.waybill || `${t("Shipment")} #${shipment.id}`}
        subtitle={`${t("To")} ${receiverLabel}, ${shipment.receiver_city}`}
        chips={
          <StatusChip
            label={shipment.status_display}
            value={shipment.status}
            colorMap={SHIPMENT_STATUS_COLOR_MAP}
          />
        }
      />

      {inFlight && (
        <Alert severity="info">
          {shipment.status === "CG"
            ? t("Furgonetka is cancelling the shipment...")
            : t("Furgonetka is confirming the order. The waybill and label appear here in a moment.")}
        </Alert>
      )}
      {shipment.last_error && (
        <Alert severity={shipment.status === "FL" ? "error" : "warning"}>{shipment.last_error}</Alert>
      )}
      {shipment.stock_released_at && (
        <Alert severity="success">
          {t("The parts went back to stock on {{date}}.", { date: formatDateTimeInline(shipment.stock_released_at) })}
        </Alert>
      )}
      {shipment.can_return_to_stock && (
        <Alert severity="warning">
          {t("The parcel was returned. Once the parts are back, book them into stock from the toolbar.")}
        </Alert>
      )}

      <DetailGrid>
        <DetailSection title={t("Receiver")}>
          {shipment.client_info && (
            <DetailRow label={t("Client")}>
              <EntityReferenceLink
                entityType="client"
                entityId={shipment.client_info.id}
                label={shipment.client_info.name}
                canPreview={canViewClients}
              />
            </DetailRow>
          )}
          <DetailRow label={t("Company")} value={shipment.receiver_company || "-"} />
          <DetailRow label={t("Contact person")} value={shipment.receiver_name} />
          <DetailRow
            label={t("Address")}
            value={`${shipment.receiver_street}, ${shipment.receiver_postcode} ${shipment.receiver_city}, ${countryName(
              shipment.receiver_country
            )}`}
          />
          <DetailRow label={t("Phone number")} value={shipment.receiver_phone} />
          <DetailRow label={t("Email")} value={shipment.receiver_email} />
        </DetailSection>

        <DetailSection title={t("Carrier")}>
          <DetailRow label={t("Service")} value={shipment.service_name || shipment.carrier} />
          <DetailRow label={t("Waybill number")} value={shipment.waybill || "-"} />
          {shipment.tracking_url && (
            <DetailRow label={t("Tracking link")}>
              <Link href={shipment.tracking_url} target="_blank" rel="noopener noreferrer">
                {t("Track at the carrier")}
              </Link>
            </DetailRow>
          )}
          <DetailRow
            label={t("Price")}
            value={
              shipment.price_gross
                ? `${formatMoney(shipment.price_gross)} ${t("gross")} (${formatMoney(shipment.price_net)} ${t("net")})`
                : "-"
            }
          />
          <DetailRow
            label={t("Parcels")}
            value={shipment.parcels
              .map((parcel) => `${parcel.width}×${parcel.depth}×${parcel.height} cm, ${formatQuantity(parcel.weight)} kg`)
              .join("; ")}
          />
          <DetailRow label={t("Ordered at")} value={formatDateTimeInline(shipment.ordered_at) || "-"} />
          <DetailRow label={t("Delivered at")} value={formatDateTimeInline(shipment.delivered_at) || "-"} />
        </DetailSection>
      </DetailGrid>

      <DetailSection title={t("Parts")}>
        {shipment.items.map((item) => (
          <DetailRow
            key={item.id}
            label={item.part_name}
            value={`${formatQuantity(item.quantity)} ${item.unit}`.trim()}
          />
        ))}
        {shipment.issued_batches.length > 0 && (
          <DetailRow label={t("Issued from orders")} alignTop>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {shipment.issued_batches.map((batch) => (
                <Box key={batch.id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <EntityReferenceLink
                    entityType="orderedPart"
                    entityId={buildOrderedPartReferenceId(batch.part_id, batch.ordered_part_id)}
                    label={batch.ordered_part_number}
                    canPreview={canViewOrderedParts}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {batch.part_name} × {formatQuantity(batch.quantity)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DetailRow>
        )}
      </DetailSection>

      <DetailSection
        title={t("Tracking")}
        description={
          shipment.tracking_synced_at
            ? t("Last checked {{date}}", { date: formatDateTimeInline(shipment.tracking_synced_at) })
            : undefined
        }
      >
        {shipment.tracking_events.length === 0 ? (
          <DetailRow label={t("Status")} value={t("No tracking events yet")} />
        ) : (
          shipment.tracking_events.map((event, index) => (
            <DetailRow
              key={`${event.datetime}-${index}`}
              label={event.datetime || "-"}
              value={[
                t(TRACKING_STATE_LABEL_KEYS[event.state] ?? event.state),
                event.status && event.status !== event.state ? event.status : null,
                event.branch || null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
          ))
        )}
      </DetailSection>

      {shipment.notes && (
        <DetailSection title={t("Notes")}>
          <DetailRow label={t("Notes")} value={shipment.notes} multiline />
        </DetailSection>
      )}

      <DetailMetadata
        createdAt={shipment.created_at}
        createdBy={shipment.created_by_name || undefined}
      />
    </DetailPage>
  );
};
